/**
 * clickable-dispatch.js -- cart-page delivery estimates + dispatch line
 * (DELIVERY-ESTIMATES Phase 2). CONTRACT: docs/cart-estimates-handoff.md
 * (supersedes docs/CONTRACT-dispatch-api.md -- the r1 adapter contract).
 *
 * Endpoints (LIVE worker; CORS-open to the storefront + myshopify preview;
 * NEVER send an Authorization header):
 *   GET  /dispatch-estimate          -> the section's dispatch line
 *   GET  /suburbs?postcode=NNNN      -> OPTIONAL in v2, no longer called
 *   POST /delivery-estimate          -> options[]; body carries cart.items
 *                                       STRAIGHT from /cart.js (no reshaping)
 *
 * Rules from the handoff:
 * - Debounce postcode ~400ms; only estimate on a 4-digit postcode.
 * - v2 POSTCODE-ONLY MODE: no suburb is ever sent -- the worker resolves
 *   the cheapest pricing class and reports resolved_suburb (informational;
 *   NOT displayed, r2 ruling) + surcharge_possible (caveat on true).
 * - Countdown computed CLIENT-SIDE from cutoff_at_ms, offset-corrected via
 *   server_time_ms (responses cache up to 60s -- never trust a server
 *   "seconds remaining"). in_gray_window -> neutral "In stock - Ready to
 *   dispatch", no countdown, no date. delay_reason label rendered as given
 *   (dispatch_label verbatim).
 * - FAIL SILENT: network error / non-JSON / ok:false (except
 *   suburb_mismatch) -> empty prompt state. Nothing may imply shipping is
 *   unavailable; checkout is the binding truth. The r1 zone-table fallback
 *   is RETIRED as a data path (fallback code deleted; the section's zone
 *   blocks remain schema/customizer content only, never rendered).
 * - suburb_mismatch cannot occur in postcode-only mode (v2) -- the retry
 *   machinery is retired.
 * - price_aud rendered VERBATIM -- no client-side money maths.
 * - Persistence: ckDeliveryDestV1 (own key, versioned; JSON object -- never
 *   the vehicle-state keys). Pre-fill + auto-fetch on return; re-fetch on
 *   cart mutation via the EXISTING js-contents truth-repaint observer (the
 *   qty-clamp refreshTruth swap re-creates the estimator nodes; a fresh
 *   bind with a stored destination re-fetches -- no second cart listener).
 *
 * Scope: cart page ONLY (this script is loaded by the cart section alone).
 * The header pill / PDP dispatch line keep their local logic until Phase 3.
 */
(function () {
  'use strict';

  /* rule-21 corollary: classic scripts execute per tag -- run-once guard */
  if (window.__ckDispatchP2) return;
  window.__ckDispatchP2 = true;

  var BASE = 'https://shipping-rates.lucas-45a.workers.dev';
  var DEST_KEY = 'ckDeliveryDestV1';
  var CACHE_KEY = 'ckDispatchCacheV1';
  var CACHE_TTL_MS = 2 * 60 * 1000;
  var DEBOUNCE_MS = 400;
  var ESTIMATE_COPY = 'Estimated — final options confirmed at checkout.';

  function esc(value) {
    var d = document.createElement('div');
    d.textContent = value == null ? '' : String(value);
    return d.innerHTML;
  }

  /* ---- destination persistence (own versioned key) ----
     r2 (Lucas ruling): POSTCODE ONLY — the estimate is an indication, not
     a quote; the suburb is a silent implementation detail sourced fresh
     from /suburbs on every run. A legacy stored suburb (the r1 shape, the
     mismatch source) is CLEARED on first read. */
  function loadDest() {
    try {
      var v = JSON.parse(localStorage.getItem(DEST_KEY));
      if (!v || !/^[0-9]{4}$/.test(String(v.postcode || ''))) return null;
      if ('suburb' in v) {
        v = { postcode: v.postcode, h: v.h || null };
        localStorage.setItem(DEST_KEY, JSON.stringify(v)); /* stale suburb cleared */
      }
      return v;
    } catch (e) { return null; }
  }
  /* h = the last rendered results-zone height: the return-visit parse-time
     reservation uses it EXACTLY (same cart -> same options -> zero shift;
     option counts vary by destination, so a fixed literal can't). */
  function saveDest(postcode, h) {
    try { localStorage.setItem(DEST_KEY, JSON.stringify({ postcode: postcode, h: h || null })); } catch (e) { /* unavailable */ }
  }

  /* =====================================================================
     DISPATCH LINE -- /dispatch-estimate
     v2 contract: countdown computed against the BROWSER's own clock from
     the absolute cutoff_at_ms instant (server_time_ms is advisory only --
     the v1 offset correction is RETIRED per the canonical spec).
     ===================================================================== */
  /* =====================================================================
     THE DISPATCH CLOCK (liveness round, 18 Aug) — Phase 3 as anticipated
     below: ONE client-side clock for every dispatch surface sitewide
     (header pill, PDP strip, cart summary line, mini-cart line). This
     module loads on every page via the drawer, so it owns:
     - offset correction: server_time_ms - Date.now() captured per fetch
       (CONTRACT DELTA, flagged: the v2 handoff retired the offset; the
       liveness brief reinstates it — device clock skew must not bend the
       countdown; the handoff doc needs the amendment recorded);
     - a 60s recompute tick from the corrected clock;
     - re-fetch on: visibilitychange->visible, window focus, cutoff
       crossing (flips to next-day and into the worker-owned gray window),
       every 60s WHILE in the gray window (its END time is worker config —
       only a fetch can see it), and a 15-min heartbeat;
     - a broadcast: 'clickable:dispatch-state' with { d, offset } after
       every fetch and every tick — the pill and PDP strip subscribe and
       keep their local computation only as the no-worker fallback.
     Paused semantics unchanged (dates nulled; each surface's own
     handling stands). All repaints are in-place text swaps — rule 29,
     zero CLS. ===================================================== */
  var dispatchState = null; /* { d, at, offset } */
  var simExtra = 0;         /* test seam: ckDispatchClock._sim() */
  var fixtureLock = false;  /* test seam: ckDispatchClock._fixture() */

  function clockNow() {
    return Date.now() + (dispatchState ? dispatchState.offset : 0) + simExtra;
  }

  function broadcast() {
    if (!dispatchState) return;
    if (window.__ckDispatchTiming && !window.__ckDispatchTiming.broadcastAt) window.__ckDispatchTiming.broadcastAt = performance.now();
    document.dispatchEvent(new CustomEvent('clickable:dispatch-state', {
      detail: { d: dispatchState.d, offset: dispatchState.offset + simExtra }
    }));
  }

  var everOk = false; /* b118: has any fetch ever succeeded this page */

  /* =====================================================================
     DISPATCH RESPONSE CACHE (b147, Lucas design 26 Aug). sessionStorage
     (per-tab, ephemeral tier -- the estimator/browse-nav precedent), key
     ckDispatchCacheV1: { v, d, offset, savedAt }. Caches the worker STATE,
     never the rendered line -- fromWorker recomputes the countdown at
     every paint. Paint-from-cache happens at module eval; the head kick's
     fetch still runs and is consumed as the confirm (no second request).
     NEVER cached: failures (writes live in the success paths only) and
     anything under fixtureLock.
     THE NON-NEGOTIABLE (two belts): a cached state that local Melbourne
     time contradicts must never paint. Belt 1 = boundary buckets -- the
     write and the read must fall in the same {date}|{00:00-13:00,
     13:00-14:45, 14:45-24:00} bucket (date in the key makes midnight a
     boundary for free). Belt 2 = a cached countdown whose own
     cutoff_at_ms has passed is dead even inside bucket A (covers a
     worker-configured early cutoff the nominal buckets can't see). */
  function melBucket(epochMs) {
    var parts = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Melbourne', year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: 'numeric', hour12: false }).formatToParts(new Date(epochMs));
    var map = {}; parts.forEach(function (p) { map[p.type] = p.value; });
    var mins = (parseInt(map.hour, 10) % 24) * 60 + parseInt(map.minute, 10);
    var band = mins < 780 ? 'A' : mins < 885 ? 'B' : 'C'; /* 13:00 / 14:45 */
    return map.year + '-' + map.month + '-' + map.day + '|' + band;
  }
  function saveCache(d, offset) {
    if (fixtureLock) return; /* QA seam must not pollute a real session */
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ v: 1, d: d, offset: offset, savedAt: Date.now() })); } catch (e) { /* unavailable */ }
  }
  function readCache() {
    var T = window.__ckDispatchTiming;
    function reject(reason) { if (T) T.cacheRejected = reason; return null; }
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return reject('absent');
      var c = JSON.parse(raw);
      if (!c || c.v !== 1 || !c.d || c.d.ok !== true || typeof c.savedAt !== 'number') return reject('shape');
      if (c.savedAt > Date.now() || Date.now() - c.savedAt >= CACHE_TTL_MS) return reject('ttl');
      if (melBucket(c.savedAt) !== melBucket(Date.now())) return reject('bucket');
      var offset = (typeof c.offset === 'number') ? c.offset : 0;
      if (c.d.dispatches_today && c.d.cutoff_at_ms && c.d.cutoff_at_ms - (Date.now() + offset) <= 0) return reject('expired-countdown');
      return { d: c.d, offset: offset, savedAt: c.savedAt };
    } catch (e) { return reject('error'); }
  }

  function fetchDispatch() {
    if (fixtureLock) return Promise.resolve(dispatchState);
    /* 26 Aug latency fix: theme.liquid's head fires an EARLY kick for this
       exact request (diagnosis: worker warm ~80-100ms, cold ~+300ms; the
       slow part was CLIENT ORDERING -- this deferred module only fetched at
       DOMContentLoaded). Consume the early response once if it is present.
       NOTE: the worker URL is duplicated in the head kick -- change both. */
    if (window.__ckDispatchEarly) {
      var early = window.__ckDispatchEarly; window.__ckDispatchEarly = null;
      return early.then(function (d) {
        if (window.__ckDispatchTiming) window.__ckDispatchTiming[(d && d.ok === true) ? 'earlyConsumed' : 'earlyUnusable'] = performance.now();
        if (!d || d.ok !== true) throw new Error('early kick unusable');
        var offset = (typeof d.server_time_ms === 'number') ? d.server_time_ms - Date.now() : 0;
        dispatchState = { d: d, at: Date.now(), offset: offset };
        everOk = true;
        saveCache(d, offset);
        broadcast();
        return dispatchState;
      }).catch(function () { window.__ckDispatchEarly = null; return fetchDispatch(); });
    }
    var ctl = ('AbortController' in window) ? new AbortController() : null;
    if (ctl) setTimeout(function () { ctl.abort(); }, 2000); /* 26 Aug: 2s hard cap -- fail open, never sit on Checking */
    return fetch(BASE + '/dispatch-estimate', ctl ? { signal: ctl.signal } : {})
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (d) {
        if (!d || d.ok !== true) throw new Error('not ok');
        var offset = (typeof d.server_time_ms === 'number') ? d.server_time_ms - Date.now() : 0;
        dispatchState = { d: d, at: Date.now(), offset: offset };
        everOk = true;
        saveCache(d, offset);
        broadcast();
        return dispatchState;
      })
      .catch(function (err) {
        /* b118: subscribers (PDP strip, header pill) hold a NEUTRAL first
           paint until the worker answers; a FIRST-fetch failure is
           broadcast so they can fail open to their local clock. A later
           refresh failure is NOT broadcast -- they keep the last-good
           state exactly as before. */
        if (!everOk) {
          document.dispatchEvent(new CustomEvent('clickable:dispatch-state', { detail: { d: null, offset: 0, failed: true } }));
        }
        throw err;
      });
  }

  function getDispatch() {
    if (dispatchState && (fixtureLock || Date.now() - dispatchState.at < 5 * 60 * 1000)) {
      return Promise.resolve(dispatchState);
    }
    return fetchDispatch();
  }

  /* Pure line composition — separated so the time-gated branches (gray
     window, each delay_reason, paused) are probeable with fixtures; the QA
     export below is the verification seam (driftCount precedent).
     v2 PAUSED SIMPLIFICATION: the worker is self-enforcing — when paused,
     every forward-looking date is null in the payload, in_gray_window is
     false, dispatches_today is false, and dispatch_label carries the
     neutral "Delivery estimate temporarily unavailable" — so the natural
     fallthrough below renders it with no special-casing (the Delta-35
     client-side suppression branch is RETIRED with the contract answer). */
  /* 26 Aug ruled state set (category-pdp pass): one local day-word helper
     shared by every surface -- say what happens next, never what didn't. */
  function nextDispatchWord() {
    var parts = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Melbourne', weekday: 'short' }).formatToParts(new Date());
    var day = ''; parts.forEach(function (p) { if (p.type === 'weekday') day = p.value; });
    return (day === 'Fri' || day === 'Sat' || day === 'Sun') ? 'Monday' : 'tomorrow';
  }

  /* ===== THE ONE LINE OWNER (26 Aug r2) — header pill, PDP strip and
     cart/drawer line all render from these two functions. States:
     countdown (before 1pm) / grey (rule) / next (tomorrow|Monday) /
     paused (worker notice verbatim; each surface picks its presentation). */
  function countdownHtml(remainMs) {
    var mins = Math.max(1, Math.floor(remainMs / 60000));
    var h = Math.floor(mins / 60), m = mins % 60;
    return 'Order within <strong>' + (h > 0 ? h + 'h ' : '') + m + 'm</strong> for dispatch today';
  }
  window.ckDispatchLine = {
    fromWorker: function (d, offset) {
      if (d.paused === true) return { state: 'paused', html: esc(d.dispatch_label) };
      /* PDP-020 ruling (26 Aug): <=767 drops "Melbourne time" (52ch wrapped
         the 1-line rule-29 slot at 375). Both spans always emitted; the
         tokens-layer fence (.ck-disp-long/.ck-disp-short) paints one per
         viewport — every surface stays on the one owner. */
      if (d.in_gray_window) return { state: 'grey', html: '<span class="ck-disp-long">Order before 1pm Melbourne time for same-day dispatch</span><span class="ck-disp-short">Order before 1pm for same-day dispatch</span>' };
      var remain = d.cutoff_at_ms ? d.cutoff_at_ms - (Date.now() + (offset || 0)) : 0;
      if (d.dispatches_today && remain > 0) return { state: 'countdown', html: countdownHtml(remain) };
      return { state: 'next', html: 'Order now — dispatched ' + nextDispatchWord() };
    },
    local: function () {
      var parts = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Melbourne', weekday: 'short', hour: 'numeric', minute: 'numeric', hour12: false }).formatToParts(new Date());
      var map = {}; parts.forEach(function (p) { map[p.type] = p.value; });
      var isWeekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].indexOf(map.weekday) !== -1;
      var minsLeft = 13 * 60 - ((parseInt(map.hour, 10) % 24) * 60 + parseInt(map.minute, 10));
      if (isWeekday && minsLeft > 0) return { state: 'countdown', html: countdownHtml(minsLeft * 60000) };
      return { state: 'next', html: 'Order now — dispatched ' + nextDispatchWord() };
    }
  };

  function dispatchLineHtml(d) {
    var r = window.ckDispatchLine.fromWorker(d, dispatchState ? dispatchState.offset : 0);
    return r.html;
  }

  /* STATIC LINE (Lucas ruling: the live timer next to a buy button reads
     as sales pressure; the static fact reads as service). Used by the
     summary line AND the mini-cart line. State-driven exactly as the
     countdown was -- only the presentation simplifies:
     - paused (strict true)      -> null = SUPPRESS the line entirely
     - gray window               -> neutral copy
     - dispatch day, pre-cutoff  -> "Order before {cutoff} Melbourne time
                                     for dispatch today" (NEVER "(AEST)" --
                                     wrong once DST starts in October;
                                     cutoff formatted from the response)
     - otherwise                 -> dispatch_label as given */
  function cutoff12(hhmm) {
    var m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ''));
    if (!m) return '1pm'; /* the standing schedule literal, fallback only */
    var h = parseInt(m[1], 10);
    var h12 = h % 12 === 0 ? 12 : h % 12;
    return h12 + (m[2] !== '00' ? ':' + m[2] : '') + (h < 12 ? 'am' : 'pm');
  }

  function staticDispatchLineHtml(d) {
    var r = window.ckDispatchLine.fromWorker(d, dispatchState ? dispatchState.offset : 0);
    if (r.state === 'paused') return null; /* drawer/cart suppress the notice */
    return r.html;
  }

  /* Delta-41 correction: the cart-top timing element is the EXISTING
     header dispatch pill (its own local logic until Phase 3 flips every
     pill instance to /dispatch-estimate) — this module paints only the
     STATIC lines (summary box + drawer). */
  function paintDispatchLine() {
    var lines = document.querySelectorAll('[data-ck-dispatch-line]');
    if (!lines.length) return;
    getDispatch().then(function (state) {
      var d = state.d;
      if (!fixtureLock && !d.in_gray_window && d.dispatches_today && d.cutoff_at_ms - clockNow() <= 0) {
        dispatchState = null; /* cutoff crossed: next tick refetches */
      }
      var staticHtml = staticDispatchLineHtml(d);
      lines.forEach(function (el) {
        var box = el.closest('[data-ck-dispatch-box]') || el.closest('[data-drawer-dispatch]') || el;
        if (staticHtml === null) {
          box.hidden = true; /* paused: suppressed entirely */
          return;
        }
        box.hidden = false;
        if (el.innerHTML !== staticHtml) el.innerHTML = staticHtml;
      });
    }).catch(function () {
      /* fail silent: the server-rendered fallback copy stands everywhere */
    });
  }

  /* QA probe surface (read-only) — fixture access to time-gated branches. */
  window.clickableDispatchProbe = { lineHtml: dispatchLineHtml, staticLineHtml: staticDispatchLineHtml };

  /* The clock's public seam. _fixture/_sim are VERIFICATION-ONLY (the
     fixture-branch precedent): _fixture(d) pins a payload and freezes
     fetches; _sim(ms) slides the corrected clock. Both drive the same
     paths customers run. */
  window.ckDispatchClock = {
    now: clockNow,
    state: function () { return dispatchState; },
    refresh: function () { lastForcedFetch = 0; forceFetch('manual'); },
    tick: function () { tickPaint(); },
    _fixture: function (d) {
      fixtureLock = d != null;
      if (d) {
        /* the seam MIRRORS the real fetch path — offset derived exactly
           as fetchDispatch derives it, so skew legs test the true code
           (probe-caught: a hardcoded 0 here made the correction
           untestable) */
        var offset = (typeof d.server_time_ms === 'number') ? d.server_time_ms - Date.now() : 0;
        dispatchState = { d: d, at: Date.now(), offset: offset };
      }
      tickPaint();
    },
    _sim: function (ms) { simExtra = ms || 0; tickPaint(); }
  };

  /* =====================================================================
     ESTIMATOR -- POST /delivery-estimate (postcode-only, v2)
     ===================================================================== */
  var flow = { postcode: null, seq: 0 };
  /* H1 (Lucas, 20 Aug): count of user-initiated estimates in flight. The
     background silent re-rate (reestimate) shares flow.seq, so a re-rate
     landing mid-flight would bump the seq and discard the customer's own
     response -- reestimate no-ops while any user estimate is pending. A
     COUNTER (not a bool) so overlapping user estimates re-enable the re-rate
     only once every one of them has settled. */
  var userPending = 0;

  function els() {
    return {
      input: document.querySelector('[data-ck-postcode]'),
      button: document.querySelector('[data-ck-postcode-check]'),
      results: document.querySelector('[data-ck-delivery-results]'),
      options: document.querySelector('[data-ck-delivery-options]'),
      note: document.querySelector('[data-ck-delivery-note]')
    };
  }

  /* Empty prompt state -- SYSTEM failures land here (handoff req 4:
     network, non-JSON, worker error -> silent; never a user-facing error).
     Deliberately a SEPARATE handler from renderUnknown (r3 item 2: the two
     paths must not collapse). */
  function renderPrompt() {
    var e = els();
    if (!e.options) return;
    e.options.innerHTML = '';
    if (e.note) e.note.hidden = true;
    if (e.results) { e.results.classList.remove('is-reserved'); e.results.style.minHeight = ''; }
  }

  /* UNKNOWN POSTCODE (r3 item 2, ruled): USER error, not system error --
     the worker answered fine but knows no such postcode (v2 probed shape:
     ok:true, options: [], resolved_suburb: null). Quiet inline message,
     muted family, recoverable -- the next input pass replaces it. */
  function renderUnknown() {
    var e = els();
    if (!e.options) return;
    e.options.innerHTML = '<p class="clickable-delivery__unknown">We couldn&rsquo;t find that postcode &mdash; double-check and try again</p>';
    if (e.note) e.note.hidden = true;
    if (e.results) { e.results.classList.remove('is-reserved'); e.results.style.minHeight = ''; }
  }

  /* WORKER-UNREACHABLE state (Lucas ruling, 20 Aug): the USER-INITIATED path
     must NEVER silently revert to the empty prompt when the customer asks and
     we fail (network / CORS / worker outage / unusable answer) -- silence
     reads as a broken store (a train tunnel, a worker down). Only the manual
     path lands here; the background silent re-rate keeps its keep-last-good
     design. Distinct from renderUnknown (a postcode typo = USER error). */
  function renderUnavailable() {
    var e = els();
    if (!e.options) return;
    e.options.innerHTML = '<p class="clickable-delivery__unavailable">Delivery estimates are temporarily unavailable &mdash; shipping is calculated at checkout.</p>';
    if (e.note) e.note.hidden = true;
    if (e.results) { e.results.classList.remove('is-reserved'); e.results.style.minHeight = ''; }
  }

  /* LOADING (r3 item 1): fetch-in-flight indicator inside the existing
     reservation (CLS 0), shown from the moment a valid postcode fires
     until render or failure -- manual entry AND return-visit auto-fetch.
     The search loading fix's vocabulary (hide zones, keep geometry, no
     indicator) does not translate to an indicator, so this is a
     token-composed treatment: one muted pulsing line -- FLAGGED. */
  function renderLoading() {
    var e = els();
    if (!e.options) return;
    if (!e.options.querySelector('.clickable-delivery__loading')) {
      e.options.innerHTML = '<p class="clickable-delivery__loading">Checking delivery options&hellip;</p>';
    }
    if (e.note) e.note.hidden = true;
  }

  /* Display transform (Lucas, 12 Aug): "Friday 14th" -> "Friday the
     14th" at RENDER time — estimator surfaces only, the worker's
     delivery_label travels untouched. Pattern-gated: only a dayname
     followed by an ordinal is rewritten; every other label shape
     renders verbatim. */
  function withThe(label) {
    return String(label).replace(
      /\b(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s+(\d{1,2}(?:st|nd|rd|th))\b/g,
      '$1 the $2'
    );
  }

  function renderOptions(data) {
    var e = els();
    if (!e.options) return;
    var rows = (data.options || []).map(function (o) {
      /* price_aud VERBATIM (req 6); delivery_label as given.
         r3 item 4 (ruled canon amendment on D1): the DELIVERY DATE is
         CO-PRIMARY with the price -- date + price are the row's two
         anchors, option name secondary. Date sits on its own line so it
         can never wrap into the price at 375.
         v2 paused: delivery_label is NULL (prices retained) -- the timing
         line is omitted rather than rendering a dangling prefix. */
      var timing = o.delivery_label
        ? '<div class="clickable-delivery__option-timing"><span class="clickable-delivery__option-eta-label">Estimated delivery</span> <span class="clickable-delivery__option-eta">' + esc(withThe(o.delivery_label)) + '</span></div>'
        : '';
      return '<div class="clickable-delivery__option">' +
        '<div class="clickable-delivery__option-main">' +
        '<div class="clickable-delivery__option-top"><span class="clickable-delivery__option-name">' + esc(o.name) + '</span><span class="clickable-delivery__option-price">$' + esc(o.price_aud) + '</span></div>' +
        timing +
        '</div></div>';
    }).join('');
    /* r2: postcode-only display — the silently resolved suburb is never
       shown (Lucas ruling outranks the contract's optional resolved_suburb
       display). v2 surcharge transparency: caveat rendered on an explicit
       true only ("false is never wrong, only sometimes silent"). */
    var caveat = data.surcharge_possible === true
      ? '<p class="clickable-delivery__surcharge">Some locations in this postcode may incur a delivery surcharge &mdash; confirmed at checkout.</p>'
      : '';
    e.options.innerHTML = '<p class="clickable-delivery__zone">Delivery to ' + esc(data.postcode) + '</p>' + rows + caveat;
    if (e.note) e.note.hidden = false; /* required copy, always visible with results */
  }

  /* v2 POSTCODE-ONLY MODE (the recommended flow, contract-canonical):
     one POST, no /suburbs call, no suburb in the body -- the worker
     resolves the postcode's localities and returns the CHEAPEST
     pricing-class's options (checkout stays truth for surcharge pockets).
     suburb_mismatch cannot occur in this mode; the retry machinery is
     RETIRED with it. */
  function runEstimate(postcode, silent) {
    var e = els();
    var seq = ++flow.seq;
    flow.postcode = postcode;
    /* SILENT re-rate (5e liveness): a forceFetch-driven refresh keeps the
       current rows on screen and swaps the fresh ones in place (rule 29 --
       no loading flash, no reflow). Only the manual / first-run path shows
       the loading indicator and reserves geometry. */
    if (!silent) {
      userPending++; /* H1: block the background re-rate while the user waits */
      renderLoading(); /* visible from the moment a valid postcode fires (manual + auto-fetch) */
      if (e.results) {
        /* geometry reserved before the response paints: the destination's own
           last-settled height when known, else the floor literal (class) */
        var dest = loadDest();
        if (dest && dest.h && dest.postcode === postcode) e.results.style.minHeight = dest.h + 'px';
        e.results.classList.add('is-reserved');
      }
    }
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return fetch(BASE + '/delivery-estimate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          /* cart.items passes straight through -- no reshaping */
          body: JSON.stringify({ postcode: postcode, items: cart.items })
        });
      })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status); /* 4xx/5xx (incl. rate_limited, unavailable): system-silent */
        return r.json();
      })
      .then(function (data) {
        if (!silent) userPending--; /* H1: this user estimate has settled */
        if (seq !== flow.seq) return; /* stale response -- a newer run superseded this one */
        if (data && data.ok === true && Array.isArray(data.options) && data.options.length) {
          renderOptions(data);
          /* SETTLE (r3 item 3): reservation holds during LOAD only; the
             container settles to natural height in the paint frame and the
             SETTLED height is stored for the next visit. */
          var zone = els().results;
          if (zone) {
            zone.style.minHeight = '';
            zone.classList.remove('is-reserved');
          }
          saveDest(postcode, zone ? Math.round(zone.getBoundingClientRect().height) : null);
          return;
        }
        if (data && data.ok === true && Array.isArray(data.options) && !data.options.length && !data.resolved_suburb) {
          /* v2 unknown-postcode shape (probed): OK + empty options + NULL
             resolved_suburb = the worker knows no such postcode -- USER
             error (r3 ruling) -> the "double-check" typo message. A populated
             resolved_suburb with empty options is a SERVICE GAP (real area,
             no options) -- it now falls through to the H2 unavailable notice
             on the user path (Lucas 20 Aug: the manual path never silently
             reverts); the background re-rate still keeps last-good. */
          if (!silent) renderUnknown();
          return;
        }
        /* H2: worker reachable but the answer is unusable (bad shape / service
           gap). User path shows the unavailable notice -- never a silent
           revert; the background re-rate keeps last-good (silent). */
        if (!silent) renderUnavailable();
      })
      .catch(function () {
        if (!silent) userPending--; /* H1: this user estimate has settled (failed) */
        /* H2: network / CORS / HTTP error = worker unreachable. User path
           shows the unavailable notice (Lucas: never a silent revert); the
           background silent re-rate keeps the last good rows (rule 29). */
        if (seq === flow.seq && !silent) renderUnavailable();
      });
  }

  function startFromInput() {
    var e = els();
    if (!e.input) return;
    var pc = String(e.input.value || '').trim();
    if (!/^[0-9]{4}$/.test(pc)) { renderPrompt(); flow.seq++; return; } /* 4-digit gate */
    runEstimate(pc);
  }

  var debounceTimer = null;

  function bindEstimator() {
    var e = els();
    if (!e.input || !e.button || e.button.hasAttribute('data-ck-bound')) return;
    e.button.setAttribute('data-ck-bound', '');

    /* BUBBLE-LEAK FIX (root cause): the postcode field sits inside
       <cart-items>, whose DELEGATED change listener (Dawn cart.js:25 ->
       onChange -> validateQuantity) sweeps ANY bubbled change event and
       runs quantity maths on it — for a step-less input `value % NaN !== 0`
       is always true, so it anchored the native "increments of" bubble to
       this field. Stop the event at its source; Dawn never sees it. */
    e.input.addEventListener('change', function (ev) {
      ev.stopPropagation();
    });
    e.input.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(startFromInput, DEBOUNCE_MS);
    });
    e.button.addEventListener('click', function () {
      clearTimeout(debounceTimer);
      startFromInput();
    });
    e.input.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        clearTimeout(debounceTimer);
        startFromInput();
      }
    });

    /* Return visit + CART MUTATION path: the truth-repaint swap re-creates
       these nodes, so every fresh bind with a stored destination re-fetches
       against the CURRENT cart (prices depend on the packed parcels). The
       flow re-rates against the CURRENT cart every time. */
    var dest = loadDest();
    if (dest) {
      e.input.value = dest.postcode;
      runEstimate(dest.postcode);
    }
  }

  /* ---- boot + the one cart-change hook (the existing observer).
     The module now also loads with the DRAWER (all pages) for the static
     mini-cart line; off-cart the estimator paths no-op (no elements). ---- */
  /* ---- the clock loop (liveness round) -------------------------------- */
  var lastRemainPositive = null;
  var lastForcedFetch = 0;

  /* Delivery-estimate liveness (5e, Lucas): the estimate rows carry
     dispatch-day-derived delivery DATES, so a forceFetch that refreshed the
     dispatch state must re-rate the SHOWN estimate too -- otherwise the
     dates go stale across a cutoff crossing or a backgrounded tab (before
     this, forceFetch refreshed the dispatch LINE only; runEstimate re-ran
     on cart mutation / fresh bind alone). Gated on an active, valid
     postcode with the results zone present: off-estimator pages (PDP,
     collections) and the drawer's static line with no postcode entered
     no-op. Silent -> no loading flash, current rows hold until the fresh
     rows swap in place. */
  function reestimate() {
    if (userPending > 0) return; /* H1: never race a user-initiated estimate */
    var pc = flow.postcode;
    if (!pc || !/^[0-9]{4}$/.test(pc)) return;
    if (!els().results) return;
    runEstimate(pc, true);
  }

  function forceFetch(reason) {
    /* under fixture the dispatch fetch is frozen, but a fixtured cutoff
       crossing still must re-rate the estimate (b15 verification path) */
    if (fixtureLock) { tickPaint(); reestimate(); return; }
    /* throttle: focus + visibility can fire together; never hammer */
    if (Date.now() - lastForcedFetch < 20 * 1000) return;
    lastForcedFetch = Date.now();
    dispatchState = null; /* cache invalidated */
    /* fetch UNCONDITIONALLY — pages without cart lines (PDP, collections)
       still need the broadcast for the pill/strip (probe-caught: the
       line-presence guard starved the clock on line-less pages) */
    getDispatch().then(function () { paintDispatchLine(); reestimate(); }).catch(function () { /* fail open */ });
  }

  function tickPaint() {
    var st = dispatchState;
    if (st && st.d) {
      var d = st.d;
      var remain = d.cutoff_at_ms ? d.cutoff_at_ms - clockNow() : null;
      /* cutoff crossing: the flip to next-day/gray is the WORKER's answer */
      if (remain !== null && lastRemainPositive === true && remain <= 0) {
        lastRemainPositive = false;
        forceFetch('cutoff-crossed');
        return;
      }
      if (remain !== null) lastRemainPositive = remain > 0;
      /* gray window's END is worker config — only a fetch can see it, so
         while inside it the tick refetches (worker caches 60s; cheap) */
      if (d.in_gray_window && !fixtureLock) {
        dispatchState = null;
      }
    }
    getDispatch().then(function () {
      paintDispatchLine();
      broadcast();
    }).catch(function () { paintDispatchLine(); });
  }

  function armClock() {
    /* PERF ROUND item 5 (Lucas ruling 21 Aug): THIS interval is the ONE
       minute clock. The tick event fires unconditionally (even when the
       worker fetch fails) so subscribers with their own cached state —
       the PDP dispatch line — can flip their countdown without owning a
       second interval. */
    setInterval(function () {
      tickPaint();
      document.dispatchEvent(new CustomEvent('clickable:dispatch-tick'));
    }, 60 * 1000);              /* the 60s recompute */
    setInterval(function () { forceFetch('heartbeat'); }, 15 * 60 * 1000);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') forceFetch('visible');
    });
    window.addEventListener('focus', function () { forceFetch('focus'); });
    /* b147: bfcache restore -- the in-memory state was frozen with the
       page; run the same contradiction gates against it. Stale (bucket
       crossed / countdown expired) => clear state AND the 20s throttle
       (Safari fires visibilitychange alongside pageshow; the throttle must
       never swallow a bfcache wake into painting a contradicted state). */
    window.addEventListener('pageshow', function (ev) {
      if (!ev.persisted) return;
      var st = dispatchState;
      var stale = !st;
      if (st && melBucket(st.at) !== melBucket(Date.now())) stale = true;
      if (st && st.d && st.d.dispatches_today && st.d.cutoff_at_ms && st.d.cutoff_at_ms - clockNow() <= 0) stale = true;
      if (stale) { dispatchState = null; lastForcedFetch = 0; }
      forceFetch('pageshow');
    });
  }

  function boot() {
    /* b148 BELT: since b146 the first broadcast fires at MODULE EVAL --
       before DCL -- so any subscriber registered inside a DCL handler
       missed it (the PDP strip did; b148 root cause). Re-broadcast the
       held state at DCL so no late subscriber can strand; in-place swap
       guards make it idempotent for everyone who already heard it. */
    broadcast();
    /* prime the clock on EVERY page — the broadcast feeds pill + strip
       even where no cart line renders */
    getDispatch().then(function () { paintDispatchLine(); }).catch(function () { /* fail open */ });
    bindEstimator();
    armClock();
    var main = document.getElementById('main-cart-items');
    var contents = main && main.querySelector('.js-contents');
    if (contents && 'MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        /* our own renders mutate inside the estimator -- ignore them, or
           the observer feeds back on itself */
        var external = mutations.some(function (m) {
          var node = m.target;
          return !(node.closest && node.closest('[data-ck-delivery-results]'));
        });
        if (!external) return;
        paintDispatchLine();
        bindEstimator();
      }).observe(contents.parentElement, { childList: true, subtree: true });
    }
    /* drawer swaps (ATC / truth repaints on any page) replace the drawer
       line node -- repaint it (qty-clamp dual-surface observer precedent) */
    var drawer = document.querySelector('cart-drawer');
    if (drawer && 'MutationObserver' in window) {
      new MutationObserver(function () { paintDispatchLine(); }).observe(drawer, { childList: true, subtree: true });
    }
  }

  /* 26 Aug: consume the early response at MODULE EVAL (end of parse), not
     at DOMContentLoaded -- heavy pages delayed consumption by seconds while
     the response sat ready. boot() still wires everything at DCL. */
  if (window.__ckDispatchTiming) window.__ckDispatchTiming.moduleEval = performance.now();
  /* b147: paint from cache instantly, fetch anyway, update on difference.
     A valid cache seeds dispatchState + broadcasts NOW; the confirm goes
     DIRECT to fetchDispatch (getDispatch's 5-min freshness check would see
     the cache-seeded state and skip -- the cache must not self-confirm),
     consuming the head kick's already-in-flight request. everOk = true on
     a cache paint: a later confirm failure keeps the cached (locally
     verified) state -- keep-last-good, and the failure is never written. */
  (function () {
    var cached = readCache();
    if (!cached) {
      getDispatch().then(function () { paintDispatchLine(); }).catch(function () { /* fail open (failure broadcast already sent) */ });
      return;
    }
    if (window.__ckDispatchTiming) { window.__ckDispatchTiming.cacheHit = true; window.__ckDispatchTiming.cacheConsumedAt = performance.now(); }
    dispatchState = { d: cached.d, at: cached.savedAt, offset: cached.offset };
    everOk = true;
    broadcast();
    paintDispatchLine();
    fetchDispatch().then(function () { paintDispatchLine(); }).catch(function () { /* cached state stands */ });
  })();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* diagnostics: run with ?ckdiag=1 (or localStorage.ckdiag='1') and read
     console.table(window.__ckDispatchTiming) -- parse-relative ms. */
  if (/[?&]ckdiag=1/.test(location.search) || (function(){try{return localStorage.ckdiag==='1'}catch(e){return false}})()) {
    setTimeout(function () { try { console.table(window.__ckDispatchTiming); } catch (e) { console.log(window.__ckDispatchTiming); } }, 4000);
  }
})();
